import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { Sparkline } from "../../components/icons";
import {
  useGovernanceVelocity,
  useGovernanceAccuracy,
  useGovernanceReplanFrequency,
  useGovernanceDecisionLatency,
  useGovernancePeerBenchmarks,
  type GovernanceMetricRow,
  type GovernancePeriod,
} from "../../hooks/useOutcomes";
import { useRunningPlays } from "../../hooks/useDashboard";
import { apiGet } from "../../api/client";
import { useQuery } from "@tanstack/react-query";

interface CompletedPlay {
  id: string;
  archetype: string;
  title?: string;
  state: string;
  ownerUserId?: string | null;
  targetWindowEnd?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  replanCount?: number;
  [k: string]: unknown;
}

interface PlayListResponse {
  items: CompletedPlay[];
  nextCursor: string | null;
}

function useCompletedPlays(workspaceId: string) {
  return useQuery<PlayListResponse, Error>({
    queryKey: ["plays", workspaceId, "completed"],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<PlayListResponse>(
        `/api/workspaces/${workspaceId}/plays?state=completed`,
      ),
  });
}

const RANGE_OPTIONS: ReadonlyArray<{
  key: "30d" | "90d" | "180d";
  label: string;
  days: number;
}> = [
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "90d", label: "Last 90 days", days: 90 },
  { key: "180d", label: "Last 180 days", days: 180 },
];

function num(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function pickValue(values: unknown, ...path: string[]): number {
  let cur: unknown = values;
  for (const k of path) {
    if (cur && typeof cur === "object" && k in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[k];
    } else {
      return 0;
    }
  }
  return num(cur);
}

function velocityCount(row: GovernanceMetricRow): number {
  // velocity values commonly carry { plays_completed: N, ... }
  const v = row.values as Record<string, unknown> | null;
  if (!v) return 0;
  if ("plays_completed" in v) return num(v["plays_completed"]);
  if ("count" in v) return num(v["count"]);
  if ("total" in v) return num(v["total"]);
  return 0;
}

function deltaPct(prev: number, cur: number): number {
  if (prev === 0) return cur === 0 ? 0 : 100;
  return Math.round(((cur - prev) / prev) * 100);
}

interface PerPlayCardProps {
  play: CompletedPlay;
  workspaceId: string;
}

function PerPlayCard({ play, workspaceId }: PerPlayCardProps) {
  const navigate = useNavigate();
  const replans = num(play["replanCount"] ?? play["replans"] ?? 0);
  const title = String(play["title"] ?? play.archetype);
  return (
    <div
      className="play-card"
      data-testid={`outcome-play-${play.id}`}
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/workspaces/${workspaceId}/plays/${play.id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          navigate(`/workspaces/${workspaceId}/plays/${play.id}`);
        }
      }}
    >
      <div className="play-head">
        <div>
          <div className="play-arch">{play.archetype}</div>
          <div className="play-title">{title}</div>
        </div>
        <div className="play-status">
          <span className="pill blue">completed</span>
        </div>
      </div>
      <div className="play-meta">
        <div>
          Replans
          <span className={`v ${replans > 0 ? "warn" : ""}`}>{replans}</span>
        </div>
        <div>
          Owner
          <span className="v">
            {String(play.ownerUserId ?? "—").slice(0, 8)}
          </span>
        </div>
        <div>
          Window
          <span className="v">
            {play.targetWindowEnd
              ? new Date(String(play.targetWindowEnd)).toLocaleDateString(
                  "en-US",
                  { month: "short", day: "numeric" },
                )
              : "—"}
          </span>
        </div>
      </div>
      <div className="play-foot">
        <span style={{ color: "var(--text-3)", fontSize: 11 }}>
          Open for outcome detail →
        </span>
      </div>
    </div>
  );
}

function OutcomesInner() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const [rangeKey, setRangeKey] = useState<"30d" | "90d" | "180d">("90d");
  const [period] = useState<GovernancePeriod>("week");

  const range = RANGE_OPTIONS.find((r) => r.key === rangeKey) ?? RANGE_OPTIONS[1]!;
  const days = range.days;

  const completed = useCompletedPlays(workspaceId);
  const running = useRunningPlays(workspaceId);
  const velocity = useGovernanceVelocity(workspaceId, period, days);
  const accuracy = useGovernanceAccuracy(workspaceId, period, days);
  const replanFreq = useGovernanceReplanFrequency(workspaceId, period, days);
  const decisionLatency = useGovernanceDecisionLatency(workspaceId, period, days);
  const peer = useGovernancePeerBenchmarks(workspaceId, period, days, 10);

  const velocityRows = velocity.data?.rows ?? [];
  const accuracyRows = accuracy.data?.rows ?? [];
  const replanRows = replanFreq.data?.rows ?? [];
  const latencyRows = decisionLatency.data?.rows ?? [];
  const peerRows = peer.data?.rows ?? [];

  const velocitySpark = useMemo(
    () => velocityRows.map(velocityCount),
    [velocityRows],
  );
  const velocityNow = velocitySpark[velocitySpark.length - 1] ?? 0;
  const velocityPrev = velocitySpark[velocitySpark.length - 2] ?? 0;
  const velocityDelta = deltaPct(velocityPrev, velocityNow);

  const accuracyDist = useMemo(() => {
    // accuracy rows expose distribution buckets in `values.distribution`
    // (best-effort) — fall back to counting samples if unknown shape.
    const buckets: Record<string, number> = {
      under: 0,
      onTarget: 0,
      over: 0,
    };
    for (const r of accuracyRows) {
      buckets["under"]! += pickValue(r.values, "under");
      buckets["onTarget"]! += pickValue(r.values, "on_target");
      buckets["over"]! += pickValue(r.values, "over");
    }
    return buckets;
  }, [accuracyRows]);

  const accuracyTotal =
    (accuracyDist["under"] ?? 0) +
    (accuracyDist["onTarget"] ?? 0) +
    (accuracyDist["over"] ?? 0);

  const replanByArchetype = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const r of replanRows) {
      const v = (r.values ?? {}) as { by_archetype?: Record<string, unknown> };
      const ba = v.by_archetype ?? {};
      for (const [arch, val] of Object.entries(ba)) {
        const inner = val as { count?: unknown; mean?: unknown } | undefined;
        const n = inner ? num(inner.count ?? inner.mean) : 0;
        acc[arch] = (acc[arch] ?? 0) + n;
      }
    }
    return acc;
  }, [replanRows]);

  const latencyP50 = useMemo(() => {
    if (latencyRows.length === 0) return 0;
    const last = latencyRows[latencyRows.length - 1]!;
    return pickValue(last.values, "p50");
  }, [latencyRows]);
  const latencyP95 = useMemo(() => {
    if (latencyRows.length === 0) return 0;
    const last = latencyRows[latencyRows.length - 1]!;
    return pickValue(last.values, "p95");
  }, [latencyRows]);

  const peerAvailable = useMemo(() => {
    if (peerRows.length === 0) return false;
    return peerRows.some((r) => {
      const v = (r.values ?? {}) as { available?: unknown };
      return v.available === true;
    });
  }, [peerRows]);

  // Filter completed plays (some APIs may return all). Be defensive.
  const completedPlays = (completed.data?.items ?? []).filter(
    (p) => p.state === "completed" || p.state === "abandoned",
  );

  return (
    <div className="main-inner" data-testid="outcomes">
      <div className="greet-row">
        <span className="greet">Outcomes</span>
        <span className="greet-date" data-testid="outcomes-window">
          {range.label.toUpperCase()}
        </span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">Realized lift, governance + benchmarks</div>
        <div className="lede">
          {completedPlays.length} completed play
          {completedPlays.length === 1 ? "" : "s"}
          {running.data ? ` · ${running.data.items.length} in flight` : ""}
        </div>
      </div>

      {/* Window selector */}
      <div
        style={{
          display: "flex",
          gap: 6,
          margin: "16px 0 18px",
          alignItems: "center",
        }}
        data-testid="outcomes-range-picker"
      >
        {RANGE_OPTIONS.map((r) => (
          <button
            key={r.key}
            type="button"
            className={`btn ${rangeKey === r.key ? "primary" : ""}`}
            data-testid={`outcomes-range-${r.key}`}
            onClick={() => setRangeKey(r.key)}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Per-play outcomes */}
      <div className="section-h">
        <span className="ttl">Completed plays</span>
        <span className="meta" data-testid="outcomes-completed-meta">
          {completedPlays.length} captured
        </span>
      </div>
      <div className="plays" data-testid="outcomes-completed-list">
        {completed.isLoading ? (
          <div className="play-card" data-testid="outcomes-completed-skeleton">
            <div
              className="animate-pulse"
              style={{
                background: "var(--bg-soft)",
                height: 18,
                width: "60%",
                borderRadius: 4,
              }}
            />
          </div>
        ) : completedPlays.length === 0 ? (
          <div className="play-card" data-testid="outcomes-completed-empty">
            <div className="play-title">No completed plays yet.</div>
            <div className="play-arch">
              Complete a play to see realized lift and attribution.
            </div>
          </div>
        ) : (
          completedPlays.map((p) => (
            <PerPlayCard key={p.id} play={p} workspaceId={workspaceId} />
          ))
        )}
      </div>

      {/* Governance dashboard */}
      <div className="section-h">
        <span className="ttl">Governance · {range.label.toLowerCase()}</span>
        <span className="meta">Velocity · accuracy · replan freq · latency</span>
      </div>

      <div className="kpis" data-testid="governance-tiles">
        <div className="kpi" data-testid="gov-velocity">
          <div className="lbl">Velocity (plays / week)</div>
          <div className="val">{velocityNow}</div>
          <div className={`delta ${velocityDelta >= 0 ? "up" : "down"}`}>
            {velocityDelta >= 0 ? "↑" : "↓"} {Math.abs(velocityDelta)}% vs prev
          </div>
          <div className="spark">
            {velocitySpark.length >= 2 ? (
              <Sparkline data={velocitySpark} color="var(--accent)" />
            ) : (
              <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                no data
              </span>
            )}
          </div>
        </div>

        <div className="kpi" data-testid="gov-accuracy">
          <div className="lbl">Accuracy distribution</div>
          <div className="val">{accuracyTotal}</div>
          <div className="delta up">samples</div>
          <div
            className="spark"
            aria-label="under / on_target / over"
            style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 28 }}
          >
            {accuracyTotal === 0 ? (
              <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                no data
              </span>
            ) : (
              [
                { k: "under", c: "var(--warn)" },
                { k: "onTarget", c: "var(--accent)" },
                { k: "over", c: "var(--accent-2)" },
              ].map((b) => {
                const pct = Math.round(
                  ((accuracyDist[b.k] ?? 0) / accuracyTotal) * 100,
                );
                return (
                  <div
                    key={b.k}
                    title={`${b.k}: ${pct}%`}
                    style={{
                      flex: 1,
                      height: `${Math.max(pct, 4)}%`,
                      background: b.c,
                      borderRadius: 2,
                      minHeight: 3,
                    }}
                  />
                );
              })
            )}
          </div>
        </div>

        <div className="kpi" data-testid="gov-replan-frequency">
          <div className="lbl">Replan frequency by archetype</div>
          <div className="val">{Object.keys(replanByArchetype).length}</div>
          <div className="delta up">archetypes</div>
          <div
            className="spark"
            style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 28 }}
          >
            {Object.keys(replanByArchetype).length === 0 ? (
              <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                no data
              </span>
            ) : (
              Object.entries(replanByArchetype)
                .slice(0, 6)
                .map(([k, v]) => {
                  const max = Math.max(...Object.values(replanByArchetype), 1);
                  const pct = Math.round((v / max) * 100);
                  return (
                    <div
                      key={k}
                      title={`${k}: ${v}`}
                      style={{
                        flex: 1,
                        height: `${Math.max(pct, 6)}%`,
                        background: "var(--accent)",
                        borderRadius: 2,
                        minHeight: 4,
                      }}
                    />
                  );
                })
            )}
          </div>
        </div>

        <div className="kpi" data-testid="gov-decision-latency">
          <div className="lbl">Decision latency (hrs)</div>
          <div className="val">
            {latencyP50.toFixed(1)}
            <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: 6 }}>
              p50
            </span>
          </div>
          <div className="delta up">{latencyP95.toFixed(1)}h p95</div>
          <div className="spark">
            {latencyRows.length >= 2 ? (
              <Sparkline
                data={latencyRows.map((r) => pickValue(r.values, "p50"))}
                color="var(--accent-2)"
              />
            ) : (
              <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                no data
              </span>
            )}
          </div>
        </div>

        <div className="kpi" data-testid="gov-peer-benchmarks">
          <div className="lbl">Peer benchmarks</div>
          {peerAvailable ? (
            <>
              <div className="val">live</div>
              <div className="delta up">cohort ≥10</div>
            </>
          ) : (
            <>
              <div className="val" style={{ fontSize: 16, color: "var(--text-3)" }}>
                —
              </div>
              <div
                className="delta"
                style={{ color: "var(--text-3)", fontWeight: 500 }}
                data-testid="gov-peer-empty"
              >
                Available with 10+ peers in cohort
              </div>
            </>
          )}
          <div className="spark">
            <span style={{ fontSize: 11, color: "var(--text-3)" }}>V1.5</span>
          </div>
        </div>
      </div>

      {(velocity.isError ||
        accuracy.isError ||
        replanFreq.isError ||
        decisionLatency.isError) && (
        <div
          style={{
            marginTop: 14,
            padding: "10px 12px",
            background: "var(--warn-soft)",
            border: "1px solid #f0dca0",
            borderRadius: 6,
            fontSize: 12,
            color: "var(--warn)",
          }}
          data-testid="governance-error-banner"
        >
          Some governance metrics could not be loaded.
        </div>
      )}
    </div>
  );
}

function OutcomesErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="main-inner" data-testid="outcomes-error">
      <div className="greet-row">
        <span className="greet">Outcomes unavailable</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">ERROR</div>
        <div className="lede">{error.message}</div>
      </div>
      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          className="btn primary"
          onClick={resetErrorBoundary}
        >
          Reload surface
        </button>
      </div>
    </div>
  );
}

export function Outcomes() {
  return (
    <ErrorBoundary FallbackComponent={OutcomesErrorFallback}>
      <OutcomesInner />
    </ErrorBoundary>
  );
}
